import React, { useEffect } from "react";
import { nowISO } from "../../utils";
import { CircularProgress, Typography } from "@mui/material";
import { SectionCard } from "../study/course/SectionCard";
import Button from "../Button";
import { makeStyles } from "tss-react/mui";
import { WordDetail } from "../study/course";
import { useStudy } from "../../pages/study/hooks";
import { addAssignmentActivitiesByCourseAndAssignee } from "../../store/study/studyBaseApi";
import { Box } from "@mui/system";
import CompleteChapterButton from "./CompleteChapterButton";
import { useCourse } from "../../pages/course/hooks";

const useStyles = makeStyles()((theme, args) => ({
    root: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        height: '100%',
        ...({
            background: args.isParent ? theme.palette.common.white : 'transparent'
        })
    },
    actionBtn: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        justifyContent: "center",
        display: "flex"
    },
}));

export default function PronounciationSummary({ data, courseId, sectionId, onComplete, onNextChapter }) {
    const { profile, synonyms } = useStudy();
    const { lastSectionChapter } = useCourse();
    const [chapter, setChapter] = React.useState({});
    const [completed, setCompleted] = React.useState(false);
    const [parsedkeywords, setParsedKeywords] = React.useState([]);

    const { classes } = useStyles({ isParent: courseId && sectionId });

    const startTime = React.useRef(nowISO());

    const handleComplete = async () => {
        if (!courseId || !sectionId) return;

        if (onComplete && typeof onComplete === "function") {
            onComplete();
        }

        setCompleted(true);

        try {
            await addAssignmentActivitiesByCourseAndAssignee({
                params: { courseId, assigneeId: profile.id }, body: {
                    assigneeId: profile.id, courseId, sectionId,
                    chapterId: chapter.chapterId,
                    endTime: nowISO(),
                    startTime: startTime.current,
                    type: 'CHAPTER_COMPLETE',
                }
            });
            if (!lastSectionChapter.chapter) {
                await onNextChapter();
            }
        } catch (e) {
            console.error(e);
        }
    }

    const onPlaybackStarted = React.useCallback(() => {
        startTime.current = nowISO();
    }, []);

    React.useEffect(() => {
        if (!data || !Object.keys(data).length) return;

        let { parsedMarkupContent, parsedmarkupKeywords, ...rest } = data;

        if (parsedmarkupKeywords && parsedmarkupKeywords.length) {
            parsedmarkupKeywords = parsedmarkupKeywords.map(e => ({ ...e, synonyms: String(e.synonyms).split(',').map(syn => syn.trim()) }))
            setParsedKeywords(parsedmarkupKeywords);
        }

        setChapter(rest);

    }, [data]);

    // Handling for older model (without markup content in keywords).
    // Course-configured keywords are authoritative — never fall back to the
    // dictionary API when they are present (keyed off `data`, not the possibly
    // stale `parsedkeywords` state, so this can't clobber the configured data).
    React.useEffect(() => {
        if (data?.parsedmarkupKeywords?.length) return;
        if (!synonyms.length) return;

        let _parsedkeywords = synonyms.map(e => {
            return {
                synonyms: e.synonyms,
                text: e.word,
                definition: e.glossary,
            }
        });

        setParsedKeywords(_parsedkeywords);
    }, [synonyms, data]);


    if (!Object.keys(chapter).length) return <></>;

    return (
        <Box className={classes.root}>
            <SectionCard style={{
                height: '100%',
                overflowY: 'scroll',
                overflowX: 'hidden', // Prevent horizontal scrolling
                wordWrap: 'break-word',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#E1E4E6',
                    borderRadius: '10px',
                },
                '&:hover, &:focus-within': {
                    '&::-webkit-scrollbar-thumb': {
                        background: 'linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)',
                        width: '12px',
                        cursor: 'pointer',
                    },
                },
            }}>
                <Typography variant="h5" sx={{ p: 2 }}>Word List</Typography>
                {parsedkeywords.length ? parsedkeywords.map((syn, index) => (
                    <WordDetail key={index} wordDetails={syn} onPlaybackStart={onPlaybackStarted} />
                )) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ marginRight: '1rem' }}>Loading Pronounciation Summary...</div>
                        <div><CircularProgress size={'25px'} /></div>
                    </Box>
                )}
            </SectionCard>

            {courseId && sectionId && !completed && <div className={classes.actionBtn}>
                <CompleteChapterButton onClick={handleComplete} />
            </div>}
        </Box>
    )
}